import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { useCheckoutSession } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionContext";
import { updateSessionCache } from "@circle-react/components/Paywalls/Checkout/helpers/sessions/updateSessionCache";
import { checkoutSessionOwnershipRequestOptions } from "@circle-react/components/Paywalls/Checkout/sessionOwnership";
import { getMainOffer } from "@circle-react/components/Paywalls/CheckoutWithSession/helpers/SessionStepQueryHelper";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useToast } from "@circleco/compass/context/CompassContext";
import { useMutation, useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { setCsrfToken } from "@/react/helpers/csrfToken";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

import {
  EMAIL_VERIFICATION_ACTION_TYPES,
  type EmailVerificationAction,
  type EmailVerificationStatus,
  type ForgotPasswordResponse,
  type OTPResendResponse,
  type OTPVerificationResponse,
  type PasswordSignInResponse,
} from "../types";

export const extractErrorMessage = (error: any): string =>
  error?.message || t("paywall_checkout.form.errors.generic_error");

interface UseEmailVerificationMutationsProps {
  dispatch: React.Dispatch<EmailVerificationAction>;
}

interface CheckoutSessionParams {
  checkout_session_id?: number;
  paywall_id?: number;
}

export const useEmailVerificationMutations = ({
  dispatch,
}: UseEmailVerificationMutationsProps) => {
  const toast = useToast();
  const success = (title: string) =>
    toast.add({
      "data-testid": "flash-notice",
      icon: "circle-check-filled",
      iconColor: "success",
      title,
      variant: "primary",
    });
  const error = (title: string) =>
    toast.add({
      "data-testid": "flash-notice",
      icon: "triangle-exclamation-filled",
      iconColor: "danger",
      title,
      timeout: 6000,
      variant: "destructive",
    });
  const { session, ownershipToken } = useCheckoutSession();
  const { refetch: refetchPunditUserContext } = usePunditUserContext();
  const queryClient = useQueryClient();

  const ownershipRequestOptions = (params: CheckoutSessionParams) =>
    checkoutSessionOwnershipRequestOptions({
      ownershipToken,
      sessionId: session?.id,
      targetSessionId: params.checkout_session_id ?? null,
    });

  const updateCachedSessionFromResponse = (
    checkoutSession: CheckoutSession,
  ) => {
    const mainOffer = getMainOffer(session?.steps[0]);
    if (!mainOffer) return;

    updateSessionCache(queryClient, mainOffer.paywall.id, checkoutSession);
  };

  const getSessionParams = (): CheckoutSessionParams => {
    if (!session?.id) return {};
    const mainOffer = getMainOffer(session.steps[0]);
    if (!mainOffer) return {};
    return {
      checkout_session_id: session.id,
      paywall_id: mainOffer.paywall.id,
    };
  };

  const createErrorHandler =
    (errorActionType: EmailVerificationAction["type"]) =>
    (errorObject: any) => {
      const errorMessage = extractErrorMessage(errorObject);
      dispatch({
        type: errorActionType,
        payload: errorMessage,
      } as EmailVerificationAction);
      error(errorMessage);
    };

  const verifyEmailMutation = useMutation<
    { status: EmailVerificationStatus },
    Error,
    string
  >({
    mutationFn: (email: string) =>
      reactQueryPost(internalApi.paywallCheckout.emailVerifications.create(), {
        email,
      }),
  });

  const verifyOTPMutation = useMutation<
    OTPVerificationResponse,
    Error,
    { email: string; otp: string }
  >({
    mutationFn: ({ email, otp }) => {
      const sessionParams = getSessionParams();
      return reactQueryPost(
        internalApi.paywallCheckout.otpSessions.create(),
        {
          user: { email, otp },
          ...sessionParams,
        },
        ownershipRequestOptions(sessionParams),
      );
    },
    onSuccess: async (response, { otp }) => {
      if (response.checkout_session) {
        try {
          await refetchPunditUserContext({ throwOnError: true });
          updateCachedSessionFromResponse(response.checkout_session);
        } catch (refreshError: unknown) {
          createErrorHandler(
            EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_ERROR,
          )(refreshError);
          return;
        }
      }
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_SUCCESS,
        payload: { otp },
      });
    },
    onError: createErrorHandler(
      EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_ERROR,
    ),
  });

  const resendOTPMutation = useMutation<OTPResendResponse, Error, string>({
    mutationFn: (email: string) =>
      reactQueryPost(authenticationAppRoutes.resendOtp(), {
        user: { email },
      }),
    onSuccess: () => {
      dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_SUCCESS });
      success(t("paywall_checkout.form.resent_otp"));
    },
    onError: createErrorHandler(
      EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_ERROR,
    ),
  });

  const signInWithPasswordMutation = useMutation<
    PasswordSignInResponse,
    Error,
    { email: string; password: string }
  >({
    mutationFn: ({ email, password }) => {
      const sessionParams = getSessionParams();
      return reactQueryPost(
        internalApi.paywallCheckout.authSessions.create(),
        {
          user: { email, password },
          ...sessionParams,
        },
        ownershipRequestOptions(sessionParams),
      );
    },
    onSuccess: async response => {
      setCsrfToken(response.csrf_token);
      if (response.checkout_session) {
        try {
          await refetchPunditUserContext({ throwOnError: true });
          updateCachedSessionFromResponse(response.checkout_session);
        } catch (refreshError: unknown) {
          createErrorHandler(
            EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_ERROR,
          )(refreshError);
          return;
        }
      }
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_SUCCESS,
      });
    },
    onError: createErrorHandler(
      EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_ERROR,
    ),
  });

  const forgotPasswordMutation = useMutation<
    ForgotPasswordResponse,
    Error,
    string
  >({
    mutationFn: (email: string) =>
      reactQueryPost(authenticationAppRoutes.resetPassword(), {
        user: { email },
      }),
    onSuccess: () => {
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_SUCCESS,
      });
      success(
        t(
          "paywall_checkout.form.email_verification_states.password_state.forgot_password.success",
        ),
      );
    },
    onError: createErrorHandler(
      EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_ERROR,
    ),
  });

  return {
    verifyEmailMutation,
    verifyOTPMutation,
    resendOTPMutation,
    signInWithPasswordMutation,
    forgotPasswordMutation,
  };
};
